import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import RefreshIcon from '@mui/icons-material/Refresh';
import api, { formatApiError } from '../api';
import { useAuth } from '../auth';
import { vendorCurrency } from '../constants';

const USD_PACKS = [25, 50, 100, 150, 250, 500];
const MIN_USD = 1;
const MAX_USD = 5000;

/** Rough USD→wallet credit preview (server recomputes exact). */
function previewCredit(usd, currency, rateOverride) {
  const rates = { AED: 3.67, SAR: 3.75, QAR: 3.64, KWD: 0.31, BHD: 0.38, OMR: 0.39, EUR: 0.92, GBP: 0.79, USD: 1 };
  const rate =
    rateOverride != null && Number.isFinite(Number(rateOverride))
      ? Number(rateOverride)
      : rates[(currency || 'AED').toUpperCase()] || 3.67;
  return Math.round(Number(usd) * rate * 100) / 100;
}

export default function TopUpPage() {
  const { vendor, refreshShop } = useAuth();
  const currency = vendorCurrency(vendor);
  const [searchParams, setSearchParams] = useSearchParams();
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [usd, setUsd] = useState(100);
  const [custom, setCustom] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [packs, setPacks] = useState([]);
  const [serverRate, setServerRate] = useState(null);
  const [serverCurrency, setServerCurrency] = useState(null);

  const displayCur = (serverCurrency || currency || 'AED').toUpperCase();

  const loadBalance = useCallback(async () => {
    setLoading(true);
    try {
      const [balRes, packsRes] = await Promise.all([
        api.get('/vendor/auth/finance/balance'),
        api.get('/vendor/auth/finance/packs').catch(() => null),
      ]);
      setBalance(balRes.data?.balance ?? balRes.data?.wallet_balance ?? null);
      if (packsRes?.data) {
        const list = packsRes.data.packs || [];
        setPacks(list);
        if (packsRes.data.credit_currency) setServerCurrency(packsRes.data.credit_currency);
        if (packsRes.data.usd_to_local_rate != null) {
          setServerRate(Number(packsRes.data.usd_to_local_rate));
        } else if (packsRes.data.usd_to_aed_rate != null) {
          setServerRate(Number(packsRes.data.usd_to_aed_rate));
        }
      }
    } catch (err) {
      setError(formatApiError(err, 'Could not load wallet balance'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadBalance();
  }, [loadBalance]);

  // Stripe return
  useEffect(() => {
    const ok = searchParams.get('success') === 'true' || searchParams.get('topup_success') === 'true';
    const canceled = searchParams.get('canceled') === 'true';
    const sessionId = searchParams.get('session_id');
    if (canceled) {
      setError('Payment canceled. You can try again when ready.');
      setSearchParams({}, { replace: true });
      return;
    }
    if (!ok || !sessionId) return;
    let cancelled = false;
    (async () => {
      setBusy(true);
      try {
        await api.post('/vendor/auth/finance/topup/confirm', { session_id: sessionId });
        if (!cancelled) {
          setSuccess('Top-up confirmed. Wallet balance updated.');
          await loadBalance();
          await refreshShop?.();
        }
      } catch (err) {
        if (!cancelled) {
          setError(
            formatApiError(
              err,
              'Payment may have succeeded — refresh in a moment; webhook can still credit the wallet.'
            )
          );
          await loadBalance();
        }
      } finally {
        if (!cancelled) {
          setBusy(false);
          setSearchParams({}, { replace: true });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [searchParams, setSearchParams, loadBalance, refreshShop]);

  const amountUsd = useMemo(() => {
    if (custom !== '') {
      const n = Number(custom);
      return Number.isFinite(n) ? n : null;
    }
    return usd;
  }, [custom, usd]);

  const matchedPack = useMemo(() => {
    if (amountUsd == null || !packs.length) return null;
    return packs.find((p) => Number(p.value_usd ?? p.value) === Number(amountUsd)) || null;
  }, [amountUsd, packs]);

  const creditPreview = useMemo(() => {
    if (amountUsd == null || amountUsd < MIN_USD) return null;
    if (matchedPack?.credit_amount != null) {
      return {
        amount: Number(matchedPack.credit_amount),
        currency: (matchedPack.credit_currency || displayCur).toUpperCase(),
        fromServer: true,
      };
    }
    return {
      amount: previewCredit(amountUsd, displayCur, serverRate),
      currency: displayCur,
      fromServer: false,
    };
  }, [amountUsd, matchedPack, displayCur, serverRate]);

  const startCheckout = async () => {
    setError('');
    setSuccess('');
    if (amountUsd == null || amountUsd < MIN_USD || amountUsd > MAX_USD) {
      setError(`Enter an amount between $${MIN_USD} and $${MAX_USD} USD`);
      return;
    }
    setBusy(true);
    try {
      const res = await api.post('/vendor/auth/finance/topup/checkout', {
        amount: amountUsd,
        credit_amount: creditPreview?.amount,
      });
      const url = res.data?.checkout_url || res.data?.url;
      if (!url) {
        setError('Checkout URL missing from server');
        setBusy(false);
        return;
      }
      // Prefer exact credit from checkout response when present
      if (res.data?.credit_amount != null || res.data?.credit_currency) {
        /* navigation away — values unused here */
      }
      window.location.href = url;
    } catch (err) {
      setError(formatApiError(err, 'Could not start Stripe checkout'));
      setBusy(false);
    }
  };

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ sm: 'center' }}
        spacing={1.5}
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant="h5" fontWeight={900}>
            Top up wallet
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Prepaid credits for GoSnel’s share on completed Neighbour (and hotel) orders. You keep
            what the customer pays you.
          </Typography>
        </Box>
        <Button startIcon={<RefreshIcon />} onClick={loadBalance} disabled={loading}>
          Refresh
        </Button>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, mb: 2 }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <AccountBalanceWalletIcon color="primary" />
          <Box>
            <Typography variant="caption" color="text.secondary" fontWeight={700}>
              Current balance
            </Typography>
            <Typography fontWeight={900} fontSize="1.35rem">
              {loading ? (
                <CircularProgress size={18} />
              ) : balance != null ? (
                `${displayCur} ${Number(balance).toFixed(2)}`
              ) : (
                '—'
              )}
            </Typography>
          </Box>
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
        <Typography fontWeight={800} sx={{ mb: 1.5 }}>
          Choose amount (USD)
        </Typography>
        <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1} sx={{ mb: 2 }}>
          {USD_PACKS.map((n) => (
            <Chip
              key={n}
              label={`$${n}`}
              color={custom === '' && usd === n ? 'primary' : 'default'}
              variant={custom === '' && usd === n ? 'filled' : 'outlined'}
              onClick={() => {
                setUsd(n);
                setCustom('');
              }}
              sx={{ fontWeight: 800 }}
            />
          ))}
        </Stack>
        <TextField
          label="Custom USD"
          value={custom}
          onChange={(e) => setCustom(e.target.value)}
          type="number"
          fullWidth
          inputProps={{ min: MIN_USD, max: MAX_USD, step: 1 }}
          helperText={`Min $${MIN_USD} · Max $${MAX_USD}`}
          sx={{ mb: 2 }}
        />

        {creditPreview != null && (
          <Paper
            elevation={0}
            sx={{
              mb: 2,
              p: 2,
              borderRadius: 2,
              bgcolor: 'rgba(15, 118, 110, 0.08)',
              border: '1px solid',
              borderColor: 'rgba(15, 118, 110, 0.25)',
            }}
          >
            <Typography variant="caption" fontWeight={800} color="text.secondary" display="block">
              Estimate in {creditPreview.currency}
            </Typography>
            <Typography fontWeight={900} fontSize="1.5rem" sx={{ color: '#0f766e', lineHeight: 1.2 }}>
              {creditPreview.currency} {creditPreview.amount.toFixed(2)}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
              {creditPreview.fromServer
                ? 'Based on server pack rates for your wallet currency.'
                : 'Local FX estimate — the server sets the exact credit amount at checkout.'}
            </Typography>
          </Paper>
        )}

        <Button
          variant="contained"
          size="large"
          onClick={startCheckout}
          disabled={busy || amountUsd == null}
          startIcon={busy ? <CircularProgress size={18} color="inherit" /> : null}
        >
          Pay with Stripe
        </Button>
      </Paper>
    </Box>
  );
}
